import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFlag } from 'flags';
import * as yup from 'yup';
import { Formik, Field, Form } from 'formik';

import { Card } from 'components/Card/Card';
import Button from 'components/Button/Button';
import FullScreen from 'components/FullScreen/FullScreen';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import colors from 'theme/Colors.module.scss';
import './UserCreation.scss';

import {
  userFieldValidations,
  userPasswordFieldValidations,
} from 'modules/users/userValidations';

import {
  Box,
  Button as RegularButton,
  Text,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  FormLabel,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';

import { paramsToObject } from 'helpers/paramHelpers';
import {
  useCreateUserMutation,
  useLazyCheckInvitationTokenQuery,
} from 'modules/users/usersApi';
import { useLoginMutation } from 'modules/app/appApi';
import { setUser, setToken } from 'modules/app/appSlice';

const FORM_DEFAULTS = {
  firstname: '',
  lastname: '',
  password: '',
  password_confirmation: '',
  email: '',
};

const schema = yup.object().shape({
  ...userFieldValidations,
  ...userPasswordFieldValidations,
});

export default () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const params = paramsToObject(search);
  const invitationToken = params.t;
  const [showPassword, setShowPassword] = useState(false);

  const sessionUser = useSelector(state => state.app.user);

  const [invitation, setInvitation] = useState(null);
  const isReadOnly = !invitation && useFlag(['features', 'isReadOnly']);

  const [createUser] = useCreateUserMutation();
  const [login] = useLoginMutation();
  const [checkInvitationToken] = useLazyCheckInvitationTokenQuery();

  useEffect(() => {
    const fetchData = async () => {
      if (!invitationToken) return;
      const invitation = await checkInvitationToken(invitationToken).unwrap();
      setInvitation(invitation);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationToken]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    const data = {
      ...values,
      source: 'website',
    };

    if (invitation) {
      data.invitation_token = invitationToken;
    } else if (sessionUser) {
      data.company_id = sessionUser.company_id;
    }

    try {
      const user = await createUser(data).unwrap();

      if (invitation) {
        const loginUser = await login({
          user: {
            email: user.email,
            password: values.password,
          },
        }).unwrap();
        dispatch(setUser(loginUser.user));
        dispatch(setToken(loginUser.token));
        navigate('/dashboard');
      } else {
        navigate(`/users/${user.id}`);
      }
    } catch (error) {
      const data = error?.data.error.data;
      Object.keys(data || {}).forEach(errorKey => {
        data[errorKey].forEach(message => {
          dispatch(
            pushNotificationWithTimeout({
              type: 'error',
              message: message,
            })
          );
        });
      });
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <Formik
      enableReinitialize
      initialValues={FORM_DEFAULTS}
      validationSchema={schema}
      onSubmit={handleSubmit}
    >
      {form => {
        const { values, isSubmitting, isValid, dirty } = form;

        if (invitation && values.email !== invitation.email) {
          form.setFieldValue('email', invitation.email);
        }

        return (
          <Form className="user-create-form__container">
            <Card className="user-create-form__card">
              <Text as="p" fontWeight="500">
                User Information
              </Text>
              <Flex flexDirection="column" gap={8} mt={8}>
                <Flex gap={8} flexWrap="wrap">
                  <Flex flexDirection="column" flex="1 1 300px" minW="0">
                    <Field name="firstname">
                      {({ field, form }) => (
                        <FormControl
                          isInvalid={
                            form.errors.firstname && form.touched.firstname
                          }
                        >
                          <FormLabel>First Name</FormLabel>
                          <Input
                            {...field}
                            placeholder="First Name*"
                            focusBorderColor={colors.primary600}
                            disabled={isReadOnly || isSubmitting}
                          />
                          <FormErrorMessage>
                            {form.errors.firstname}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Flex>
                  <Flex flexDirection="column" flex="1 1 300px" minW="0">
                    <Field name="lastname">
                      {({ field, form }) => (
                        <FormControl
                          isInvalid={
                            form.errors.lastname && form.touched.lastname
                          }
                        >
                          <FormLabel>Last Name</FormLabel>
                          <Input
                            {...field}
                            placeholder="Last Name*"
                            focusBorderColor={colors.primary600}
                            disabled={isReadOnly || isSubmitting}
                          />
                          <FormErrorMessage>
                            {form.errors.lastname}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Flex>
                </Flex>

                <hr
                  style={{
                    marginTop: '0',
                    marginBottom: '-1rem',
                  }}
                />

                <Flex flexDirection="column">
                  <Field name="password">
                    {({ field, form }) => (
                      <FormControl
                        isInvalid={
                          form.errors.password && form.touched.password
                        }
                      >
                        <FormLabel>Password*</FormLabel>
                        <InputGroup size="md">
                          <Input
                            pr="4.5rem"
                            {...field}
                            placeholder="Password*"
                            focusBorderColor={colors.primary600}
                            disabled={isReadOnly || isSubmitting}
                            type={showPassword ? 'text' : 'password'}
                          />
                          <InputRightElement width="4.5rem">
                            <RegularButton
                              h="1.75rem"
                              size="sm"
                              onClick={() => {
                                setShowPassword(!showPassword);
                              }}
                            >
                              {showPassword ? (
                                <i className="fas fa-eye-slash" />
                              ) : (
                                <i className="fas fa-eye" />
                              )}
                            </RegularButton>
                          </InputRightElement>
                        </InputGroup>
                        <FormErrorMessage>
                          {form.errors.password}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Flex>

                <Flex flexDirection="column">
                  <Field name="password_confirmation">
                    {({ field, form }) => (
                      <FormControl
                        isInvalid={
                          form.errors.password_confirmation &&
                          form.touched.password_confirmation
                        }
                      >
                        <FormLabel>Password Confirmation*</FormLabel>
                        <InputGroup size="md">
                          <Input
                            pr="4.5rem"
                            {...field}
                            placeholder="Password Confirmation*"
                            focusBorderColor={colors.primary600}
                            disabled={isReadOnly || isSubmitting}
                            type={showPassword ? 'text' : 'password'}
                          />
                          <InputRightElement width="4.5rem">
                            <RegularButton
                              h="1.75rem"
                              size="sm"
                              onClick={() => {
                                setShowPassword(!showPassword);
                              }}
                            >
                              {showPassword ? (
                                <i className="fas fa-eye-slash" />
                              ) : (
                                <i className="fas fa-eye" />
                              )}
                            </RegularButton>
                          </InputRightElement>
                        </InputGroup>
                        <FormErrorMessage>
                          {form.errors.password_confirmation}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Flex>

                <hr
                  style={{
                    marginTop: '0',
                    marginBottom: '-1rem',
                  }}
                />

                <Flex flexDirection="column">
                  <Field name="email">
                    {({ field, form }) => (
                      <FormControl
                        isInvalid={form.errors.email && form.touched.email}
                      >
                        <FormLabel>Email</FormLabel>
                        <Input
                          {...field}
                          type="email"
                          placeholder="Email*"
                          focusBorderColor={colors.primary600}
                          disabled={isReadOnly || isSubmitting}
                        />
                        <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Flex>
              </Flex>
            </Card>

            {!isReadOnly && (
              <Flex flexDirection="column" align="center">
                <Button
                  icon="plus"
                  iconColor={colors.white}
                  type="submit"
                  text="Create User"
                  textColor={colors.white}
                  btnStyle="solid"
                  hoverColor={colors.primary500}
                  textHoverColor={colors.white}
                  disabled={isReadOnly || isSubmitting || !isValid || !dirty}
                />
                <Text as="p" className="footnote">
                  An asterisk (*) marks a required field.
                </Text>
              </Flex>
            )}
          </Form>
        );
      }}
    </Formik>
  );

  return (
    <>
      <Box className="user-create-header">
        <Box className="user-create-header__left">
          <Button
            type="button"
            url="/users"
            icon="back"
            text="Back"
            textColor={colors.slate}
            btnStyle="transparent"
          />
        </Box>
        <Box className="user-create-header__nav">
          <Text as="p" fontWeight="600">
            Create new user
          </Text>
        </Box>
        <Box className="user-create-header__right"></Box>
      </Box>
      {invitationToken ? <FullScreen>{content}</FullScreen> : content}
    </>
  );
};
